'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, ArrowRight, Newspaper, MapPin, Phone, Mail, FileText } from 'lucide-react';

interface Service {
  id: number;
  service_name: string;
  service_key: string;
  description: string | null;
  features: string[] | null;
  is_online: boolean | null;
}

interface Article {
  id: number;
  title: string;
  summary: string | null;
  color?: string;
  published_at: string | null;
}

// 示例文章数据（静态数据，确保页面能正常显示）
const sampleArticles = [
  {
    id: 1,
    title: '中科企服助力企业参与国家标准制定，提升行业话语权',
    summary: '近日，中科企服成功帮助多家企业参与国家标准制定工作，助力企业在行业内获得更大话语权，提升品牌影响力。',
    color: 'from-blue-500 to-blue-700',
    published_at: '2024-01-15',
  },
  {
    id: 2,
    title: '科技成果评价服务升级，助力企业创新发展',
    summary: '中科企服科技成果评价服务全面升级，新增专利价值评估、技术成熟度评价等模块，为企业创新提供更有力支撑。',
    color: 'from-green-500 to-green-700',
    published_at: '2024-01-10',
  },
  {
    id: 3,
    title: '贸易增量服务助力中小企业拓展市场',
    summary: '中科企服贸易增量服务通过资源整合、渠道对接等方式，帮助中小企业快速拓展市场，实现业绩增长。',
    color: 'from-orange-500 to-orange-700',
    published_at: '2024-01-05',
  },
];

// 全国重点城市列表（静态数据）
const keyCities = [
  // 直辖市
  { name: '北京', pinyin: 'beijing' },
  { name: '上海', pinyin: 'shanghai' },
  { name: '天津', pinyin: 'tianjin' },
  { name: '重庆', pinyin: 'chongqing' },
  // 广东省
  { name: '广州', pinyin: 'guangzhou' },
  { name: '深圳', pinyin: 'shenzhen' },
  { name: '东莞', pinyin: 'dongguan' },
  { name: '佛山', pinyin: 'foshan' },
  { name: '珠海', pinyin: 'zhuhai' },
  // 江苏省
  { name: '南京', pinyin: 'nanjing' },
  { name: '苏州', pinyin: 'suzhou' },
  { name: '无锡', pinyin: 'wuxi' },
  { name: '常州', pinyin: 'changzhou' },
  // 浙江省
  { name: '杭州', pinyin: 'hangzhou' },
  { name: '宁波', pinyin: 'ningbo' },
  { name: '温州', pinyin: 'wenzhou' },
  // 四川省
  { name: '成都', pinyin: 'chengdu' },
  // 陕西省
  { name: '西安', pinyin: 'xian' },
  // 河南省
  { name: '郑州', pinyin: 'zhengzhou' },
  // 湖南省
  { name: '长沙', pinyin: 'changsha' },
  // 湖北省
  { name: '武汉', pinyin: 'wuhan' },
  { name: '宜昌', pinyin: 'yichang' },
  { name: '襄阳', pinyin: 'xiangyang' },
  // 山东省
  { name: '济南', pinyin: 'jinan' },
  { name: '青岛', pinyin: 'qingdao' },
  { name: '烟台', pinyin: 'yantai' },
  // 安徽省
  { name: '合肥', pinyin: 'hefei' },
  // 福建省
  { name: '福州', pinyin: 'fuzhou' },
  { name: '厦门', pinyin: 'xiamen' },
  { name: '泉州', pinyin: 'quanzhou' },
  // 江西省
  { name: '南昌', pinyin: 'nanchang' },
  // 河北省
  { name: '石家庄', pinyin: 'shijiazhuang' },
  // 山西省
  { name: '太原', pinyin: 'taiyuan' },
  // 辽宁省
  { name: '沈阳', pinyin: 'shenyang' },
  { name: '大连', pinyin: 'dalian' },
  // 吉林省
  { name: '长春', pinyin: 'changchun' },
  // 黑龙江省
  { name: '哈尔滨', pinyin: 'harbin' },
  // 云南省
  { name: '昆明', pinyin: 'kunming' },
  // 贵州省
  { name: '贵阳', pinyin: 'guiyang' },
  // 广西
  { name: '南宁', pinyin: 'nanning' },
  // 海南省
  { name: '海口', pinyin: 'haikou' },
  { name: '三亚', pinyin: 'sanya' },
  // 甘肃省
  { name: '兰州', pinyin: 'lanzhou' },
  // 内蒙古
  { name: '呼和浩特', pinyin: 'huhehaote' },
  // 新疆
  { name: '乌鲁木齐', pinyin: 'wulumuqi' },
];

export default function HomePage() {
  const [services, setServices] = useState<Service[]>([]);
  const [articles, setArticles] = useState<Article[]>(sampleArticles);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [servicesRes, articlesRes] = await Promise.all([
          fetch('/api/services'),
          fetch('/api/articles?pageSize=6'),
        ]);
        const servicesData = await servicesRes.json();
        const articlesData = await articlesRes.json();
        setServices(servicesData.data || []);
        // 如果API返回了文章数据，使用API数据；否则使用示例数据
        if (articlesData.data && articlesData.data.length > 0) {
          setArticles(articlesData.data);
        }
      } catch (error) {
        console.error('获取数据失败:', error);
        // 出错时保持使用示例数据
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">加载中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航 */}
      <header className="bg-[#1e3a8a] text-white py-4 sticky top-0 z-50 shadow-md">
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between">
            <Link href="/" className="text-2xl font-bold">
              中科企服
            </Link>
            <div className="flex gap-6">
              {services.map((service) => (
                <Link
                  key={service.id}
                  href={`/services/${service.service_key}`}
                  className="hover:text-blue-300 transition-colors"
                >
                  {service.service_name}
                </Link>
              ))}
              <Link
                href="/articles"
                className="hover:text-blue-300 transition-colors flex items-center gap-1"
              >
                <Newspaper className="h-4 w-4" />
                新闻资讯
              </Link>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero区域 */}
      <section className="bg-gradient-to-r from-[#1e3a8a] to-[#3b82f6] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">中科企服科技服务平台</h1>
          <p className="text-xl mb-8">专业科技服务，助力企业创新发展</p>
          <div className="flex justify-center gap-4">
            <Link href="/cities">
              <Button size="lg" variant="secondary" className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                选择城市
              </Button>
            </Link>
            <Link href="/standard">
              <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10">
                了解服务
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 业务板块 */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 text-[#1e3a8a]">核心业务</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            我们提供全方位的科技服务解决方案，助力企业实现标准化建设、科技成果转化和业务增长
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="hover:shadow-lg transition-all hover:-translate-y-1">
                <CardHeader>
                  <CardTitle className="text-[#1e3a8a]">{service.service_name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    {service.description || '专业服务，助力企业发展'}
                  </p>
                  <Link href={`/services/${service.service_key}`}>
                    <Button className="w-full bg-[#1e3a8a] hover:bg-[#1e40af]">了解更多</Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 服务城市 - 全国覆盖 */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4 text-[#1e3a8a]">全国服务网络</h2>
          <p className="text-center text-gray-600 mb-12">
            覆盖全国{keyCities.length}个重点城市，提供本地化专业科技服务
          </p>
          
          <div className="grid grid-cols-5 md:grid-cols-8 lg:grid-cols-10 gap-2 max-w-6xl mx-auto">
            {keyCities.map((city) => (
              <Link
                key={city.pinyin}
                href={`/cities/${city.pinyin}`}
                className="bg-gray-50 px-2 py-2 rounded text-center hover:bg-[#1e3a8a] hover:text-white transition-all text-sm"
              >
                {city.name}
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link href="/cities">
              <Button variant="outline" className="border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white">
                查看全部服务城市
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 新闻资讯 */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-12">
            <h2 className="text-3xl font-bold text-[#1e3a8a]">新闻资讯</h2>
            <Link href="/articles">
              <Button variant="outline" className="flex items-center gap-2">
                查看更多
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.length === 0 ? (
              <div className="col-span-full text-center py-12 text-gray-500">
                暂无新闻资讯
              </div>
            ) : (
              articles.map((article) => {
                // 根据文章ID设置不同颜色
                const gradients: Record<number, string> = {
                  1: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  2: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)',
                  3: 'linear-gradient(135deg, #f97316 0%, #c2410c 100%)',
                };
                return (
                  <Link key={article.id} href={`/articles/${article.id}`}>
                    <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group">
                      <div 
                        className="h-48 flex items-center justify-center relative overflow-hidden"
                        style={{ background: gradients[article.id] || gradients[1] }}
                      >
                        <FileText className="w-16 h-16 text-white/30" />
                      </div>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        {article.published_at && (
                          <span className="text-sm text-gray-500 flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {new Date(article.published_at).toLocaleDateString('zh-CN')}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h3>
                      {article.summary && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {article.summary}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              );
            })
            )}
          </div>
        </div>
      </section>

      {/* 页脚 */}
      <footer className="bg-[#1e3a8a] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-bold mb-4">中科企服科技（湖北）有限公司</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                专业科技服务平台，提供标准参编、科技成果评价、贸易增量等核心业务，助力企业创新发展。
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">核心业务</h3>
              <div className="space-y-2">
                <Link href="/standard" className="block text-gray-400 hover:text-white transition-colors">
                  标准参编
                </Link>
                <Link href="/evaluation" className="block text-gray-400 hover:text-white transition-colors">
                  科技成果评价
                </Link>
                <Link href="/trade" className="block text-gray-400 hover:text-white transition-colors">
                  贸易增量
                </Link>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">快速链接</h3>
              <div className="space-y-2">
                <Link href="/cities" className="block text-gray-400 hover:text-white transition-colors">
                  服务城市
                </Link>
                <Link href="/articles" className="block text-gray-400 hover:text-white transition-colors">
                  新闻资讯
                </Link>
                <Link href="/admin/login" className="block text-gray-400 hover:text-white transition-colors">
                  后台管理
                </Link>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-4">联系我们</h3>
              <div className="space-y-3">
                <p className="text-gray-400 text-sm flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  服务热线：4000757209
                </p>
                <p className="text-gray-400 text-sm flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  邮箱：781260356@qq.com
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>© {new Date().getFullYear()} 中科企服科技（湖北）有限公司 版权所有</p>
          </div>
        </div>
      </footer>
    </div>
  );
}